/**
 * Validate a `?redirect=` query param before navigating to it.
 * Only same-origin absolute paths are allowed (open-redirect protection).
 *
 * A prefix check is not enough: browsers normalise `\` to `/` and strip
 * tab/newline characters, so `/\evil.com` or `/<TAB>/evil.com` pass a
 * `startsWith('/') && !startsWith('//')` test and still navigate off-site.
 * Resolving the value against a fixed base and comparing origins applies the
 * browser's own URL parser, so every such variant is caught.
 */
export function isSafeRedirectPath(url: string | null): url is string {
  if (!url || !url.startsWith('/')) return false;
  const base = 'https://app.invalid';
  try {
    return new URL(url, base).origin === base;
  } catch {
    return false;
  }
}

/**
 * Append a vetted `?redirect=` deep link to an auth route. Every hop of the
 * sign-in chain (protected layout → login ⇄ register → verify-email) carries
 * the same value, query string included: the OAuth consent screen
 * (`/connect?client_id=…&state=…`) is useless without its query.
 */
export function withRedirectParam(path: string, redirect: string | null): string {
  return isSafeRedirectPath(redirect)
    ? `${path}?redirect=${encodeURIComponent(redirect)}`
    : path;
}
